import React, { type ComponentProps, useCallback, useEffect } from 'react';
import { Tabs, useGlobalSearchParams, usePathname, useRouter, useSegments } from 'expo-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';

import { BodyText, Icon, Navbar, NavbarBackLink, theme } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import { useBagCount } from '../../../../hooks';
import { useNavigateBack } from '../../../../navigation';
import { MENU_VIEWS, NAVBAR_BAG_ICON_SIZE, NAVBAR_TAB_NAMES, TABS } from '../../NavbarV2.constant';
import { navbarV2Messages as messages } from '../../NavbarV2.messages';
import { NavbarLink } from '../NavbarLink';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarXS = (props: Props) => {
  const { tabBarProps } = props;

  const router = useRouter();
  const segments = useSegments();
  const currentPath = usePathname();
  const { category, view } = useGlobalSearchParams<MenuSearchParams>();
  const navigateBack = useNavigateBack();

  const { formatMessage } = useIntl();
  const { track } = useTelemetry();
  const { bagCount } = useBagCount();

  const activeTabIndexSV = useSharedValue(
    getActiveTabIndex(
      getActiveTabName({
        segments,
        stateActiveTabName: tabBarProps.state.routes[tabBarProps.state.index]?.name,
      }),
    ),
  );

  // ─── Derived ────────────────────────────────────────────────────────

  const stateActiveRoute = tabBarProps.state.routes[tabBarProps.state.index];
  const stateActiveTabName = stateActiveRoute?.name;
  const activeTabName = getActiveTabName({ segments, stateActiveTabName });

  const isAccountTabActive = activeTabName === TABS.ACCOUNT;
  const isRewardsTabActive = activeTabName === TABS.REWARDS;
  const isGiftTabActive = activeTabName === TABS.GIFT;
  const isMenuTabActive = activeTabName === TABS.MENU;
  const isProductMenuView = view === MENU_VIEWS.MENU;
  const hasCategoryParam = category !== undefined;

  const shouldRenderCompactNavbar = (segments.length >= 3 && !isMenuTabActive) || (isMenuTabActive && (hasCategoryParam || isProductMenuView)) || isGiftTabActive || isRewardsTabActive; // prettier-ignore
  const hasActiveBag = bagCount > 0;

  const routes = shouldRenderCompactNavbar
    ? tabBarProps.state.routes.filter(checkIfHomeRoute)
    : tabBarProps.state.routes;

  // ─── Handlers ───────────────────────────────────────────────────────

  const handleBackPress = useCallback(() => {
    track('back_selected', { currentPath });

    if (isAccountTabActive) {
      router.dismissTo('/account');
      return;
    }

    if (isGiftTabActive || isRewardsTabActive) {
      router.replace('/account');
      return;
    }

    if (isMenuTabActive && (hasCategoryParam || isProductMenuView)) {
      router.setParams({ category: undefined, view: MENU_VIEWS.CATEGORIES });
      return;
    }

    navigateBack();
  }, [currentPath, hasCategoryParam, isAccountTabActive, isGiftTabActive, isMenuTabActive, isProductMenuView, isRewardsTabActive, navigateBack, router, track]); // prettier-ignore

  const handleHomePress = useCallback(() => {
    track('nav_bar_home_selected');
    navigateToTab({ ...tabBarProps, tabName: TABS.HOME });
  }, [tabBarProps, track]);

  const handleMenuPress = useCallback(() => {
    track('nav_bar_menu_selected');
    navigateToTab({ ...tabBarProps, tabName: TABS.MENU });
  }, [tabBarProps, track]);

  const handleAccountPress = useCallback(() => {
    track('nav_bar_account_selected');
    navigateToTab({ ...tabBarProps, tabName: TABS.ACCOUNT });
  }, [tabBarProps, track]);

  const handleStartOrderPress = useCallback(() => {
    track('nav_bar_start_order_selected');
    navigateToTab({ ...tabBarProps, tabName: TABS.MENU });
  }, [tabBarProps, track]);

  const handleBagPress = useCallback(() => {
    track('nav_bar_bag_selected');
    router.navigate('/bag');
  }, [router, track]);

  const renderBagIcon = useCallback(() => {
    return <NavbarBagIcon bagCount={bagCount} />;
  }, [bagCount]);

  // ─── Effects ────────────────────────────────────────────────────────

  useEffect(() => {
    activeTabIndexSV.value = getActiveTabIndex(activeTabName);
  }, [activeTabIndexSV, activeTabName]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Navbar.Container>
      <Navbar.LinksGroup>
        {shouldRenderCompactNavbar ? (
          <NavbarBackLink
            accessibilityLabel={formatMessage(messages.goBack)}
            onPress={handleBackPress}
          />
        ) : undefined}

        {routes.map((route) => (
          <NavbarLink
            key={route.key}
            route={route}
            activeTabIndexSV={activeTabIndexSV}
            isActive={route.name === activeTabName}
            onAccountPress={handleAccountPress}
            onHomePress={handleHomePress}
            onMenuPress={handleMenuPress}
          />
        ))}
      </Navbar.LinksGroup>

      {hasActiveBag ? (
        <Navbar.Button rightElement={renderBagIcon} onPress={handleBagPress}>
          <FormattedMessage {...messages.bag} />
        </Navbar.Button>
      ) : null}

      {!hasActiveBag && !isMenuTabActive ? (
        <Navbar.Button onPress={handleStartOrderPress}>
          <FormattedMessage {...messages.startOrder} />
        </Navbar.Button>
      ) : null}
    </Navbar.Container>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const NavbarBagIcon = (props: NavbarBagIconProps) => {
  const { bagCount } = props;

  return (
    <View style={styles.bagIconContainer}>
      <Icon
        name="IconBagFill"
        width={NAVBAR_BAG_ICON_SIZE}
        height={NAVBAR_BAG_ICON_SIZE}
        color={theme.colors.WHITE}
      />

      <BodyText sizeMatch={['12']} style={styles.bagIconLabel}>
        {bagCount > 9 ? '9+' : bagCount}
      </BodyText>
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function navigateToTab(params: NavigateToTabParams) {
  const { descriptors, navigation, state, tabName } = params;

  const route = state.routes.find((stateRoute) => stateRoute.name === tabName);

  if (!route) {
    return;
  }

  const event = navigation.emit({ type: 'tabPress', target: route.key, canPreventDefault: true });

  if (event.defaultPrevented) {
    return;
  }

  const options = descriptors[route.key].options as TabOptions;

  options.onPress?.();
  navigation.navigate(tabName, undefined, { merge: true });
}

function checkIfHomeRoute(route: TabRoute) {
  return route.name === TABS.HOME;
}

function getActiveTabIndex(routeName?: string) {
  return NAVBAR_TAB_NAMES.findIndex((tabName) => tabName === routeName);
}

function getActiveTabName(params: GetActiveTabNameParams) {
  const { segments, stateActiveTabName } = params;

  if (segments.some((segment) => segment === TABS.MENU || segment === TABS.MENU_SINGLE)) {
    return TABS.MENU;
  }

  if (segments.some((segment) => segment === TABS.ACCOUNT)) {
    return TABS.ACCOUNT;
  }

  if (segments.some((segment) => segment === TABS.REWARDS)) {
    return TABS.REWARDS;
  }

  if (segments.some((segment) => segment === TABS.GIFT)) {
    return TABS.GIFT;
  }

  if (segments.some((segment) => segment === TABS.HOME || segment === TABS.HOME_SINGLE)) {
    return TABS.HOME;
  }

  return stateActiveTabName;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  bagIconContainer: {
    marginLeft: theme.spacing['1'],
    marginTop: -2, // to better align with label
  },
  bagIconLabel: {
    position: 'absolute',
    right: 0,
    bottom: 1,
    left: 0,
    textAlign: 'center',
    color: theme.colors.KALE,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type Props = {
  tabBarProps: TabBarProps;
};

type TabBarProps = Parameters<NonNullable<ComponentProps<typeof Tabs>['tabBar']>>[0];
type TabRoute = TabBarProps['state']['routes'][number];
type TabOptions = { onPress?: () => void };

type NavigateToTabParams = TabBarProps & { tabName: NavbarTabName };
type NavbarTabName = typeof TABS.HOME | typeof TABS.MENU | typeof TABS.ACCOUNT;
type NavbarBagIconProps = { bagCount: number };
type GetActiveTabNameParams = { segments: readonly string[]; stateActiveTabName?: string };
type MenuView = (typeof MENU_VIEWS)[keyof typeof MENU_VIEWS];
type MenuSearchParams = { category?: string | string[]; view?: MenuView | MenuView[] };
